import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Ticket } from "lucide-react";
import { ASSETS } from "../../data/assets";
import { PillButton } from "../ui/PillButton";
import { cn } from "../../lib/utils";

const LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/tickets", label: "Tickets" },
  { to: "/activities", label: "Activities" },
  { to: "/event-info", label: "Event Info" },
  { to: "/faq", label: "FAQ" },
  { to: "/my-booking", label: "My Booking" },
];

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open;

  return (
    <header
      className={cn(
        "sticky top-0 z-50 transition-all duration-300",
        solid
          ? "bg-white/85 backdrop-blur-md border-b-4 border-white shadow-playful"
          : "bg-transparent border-b-4 border-transparent"
      )}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 sm:h-20">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="navbar-logo">
          <img src={ASSETS.logo} alt="Balita Lili Tata Run 2026" className="h-11 sm:h-14 w-auto" />
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "font-display text-[15px] px-4 py-2 rounded-full transition-colors",
                  isActive ? "bg-sky/30 text-sea-deep" : "text-sea-deep/80 hover:bg-sky/20"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <PillButton
            size="sm"
            className="hidden sm:inline-flex"
            onClick={() => navigate("/tickets")}
            data-testid="navbar-get-tickets"
          >
            <Ticket className="w-4 h-4" /> GET TICKETS
          </PillButton>
          <button
            className="lg:hidden p-2 rounded-full bg-sky/20 text-sea-deep"
            onClick={() => setOpen((o) => !o)}
            aria-label="Menu"
            data-testid="navbar-hamburger"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </nav>

      {open && (
        <div className="lg:hidden bg-white border-t-2 border-sky/30 px-4 py-4 space-y-1" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              onClick={() => setOpen(false)}
              data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                cn(
                  "block font-display text-lg px-4 py-3 rounded-2xl",
                  isActive ? "bg-sky/30 text-sea-deep" : "text-sea-deep/80"
                )
              }
            >
              {l.label}
            </NavLink>
          ))}
          <PillButton
            className="w-full mt-2"
            onClick={() => { setOpen(false); navigate("/tickets"); }}
            data-testid="mobile-get-tickets"
          >
            <Ticket className="w-5 h-5" /> GET TICKETS
          </PillButton>
        </div>
      )}
    </header>
  );
}
